import React, { FC, ReactNode } from 'react';
import { useReportContext } from '../report.context';
import { useReportPageStyles } from '../report.styles';
import { VerticalAlignment } from '../report.model';
import { useGetScalingSize } from '../report-style.helpers';
import { REPORT_PAGE_DEFAULT_PADDING } from '../report.constants';

const DEFAULT_VERTICAL_ALIGNMENT = 'stretch';

/**
 * @internal
 * A page component for a report.
 *
 * The root is width-locked 16:9. Children render in an out-of-flow fill box so
 * in-flow content cannot stretch the page. Pages must fit that box; they must
 * not overflow.
 */
export const ReportPage: FC<{
  children: ReactNode;
  verticalAlign?: VerticalAlignment;
  padding?: number;
  /**
   * Skip the in-flow footer row. With {@link padding} `0` the footer is
   * overlaid at the bottom of the page instead of omitted.
   */
  hideFooter?: boolean;
  /** Overrides the report-level footer for this page. */
  footer?: ReactNode;
}> = ({
  children,
  verticalAlign = DEFAULT_VERTICAL_ALIGNMENT,
  padding = REPORT_PAGE_DEFAULT_PADDING,
  hideFooter = false,
  footer: pageFooter,
}) => {
  const {
    header,
    footer: contextFooter,
    aspectRatio,
    isFullscreen,
  } = useReportContext();
  const footer = pageFooter ?? contextFooter;
  const getScalingSize = useGetScalingSize();
  const hasHeader = Boolean(header);
  const fullBleed = hideFooter && padding === 0;
  const { classes } = useReportPageStyles({
    verticalAlign,
    padding,
    aspectRatio,
    isFullscreen,
    hasHeader,
    hideFooter,
    getScalingSize,
  });

  if (fullBleed) {
    return (
      <div className={classes.reportPageRoot} data-report-page>
        <div className={classes.reportPageFill}>{children}</div>
        {footer ? (
          <div className={classes.reportPageFooterOverlay}>{footer}</div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={classes.reportPageRoot} data-report-page>
      <div className={classes.reportPage}>
        {hasHeader ? <div>{header}</div> : null}
        <div className={classes.reportPageContentWrapper}>
          <div className={classes.reportPageContent}>{children}</div>
        </div>
        {!hideFooter && footer ? (
          <div className={classes.reportPageFooter}>{footer}</div>
        ) : null}
      </div>
    </div>
  );
};
